import { useState } from 'react'
import type { Company, Contact } from '@/entities/company'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { toast } from '@/shared/ui/toast'
import { useEditCompany } from '../api/edit'

interface ContactDialogProps {
  company: Company
  /** Индекс редактируемого контакта; undefined — добавление нового. */
  index?: number
  open: boolean
  onClose: () => void
}

// Форма одного контакта. PATCH шлёт целиком заменённый массив `contacts`
// (replace-семантика): добавление дописывает, правка заменяет по индексу.
export function ContactDialog({ company, index, open, onClose }: ContactDialogProps) {
  const edit = useEditCompany(company.id)
  const existing = index != null ? company.contacts[index] : undefined
  const [form, setForm] = useState<Contact>(existing ?? { name: '' })
  const [nameError, setNameError] = useState<string>()

  function set(patch: Partial<Contact>) {
    setForm((f) => ({ ...f, ...patch }))
  }

  function submit() {
    const name = form.name.trim()
    if (!name) {
      setNameError('Укажите имя')
      return
    }
    setNameError(undefined)
    const contact: Contact = {
      name,
      ...(form.job_title?.trim() ? { job_title: form.job_title.trim() } : {}),
      ...(form.phone?.trim() ? { phone: form.phone.trim() } : {}),
      ...(form.email?.trim() ? { email: form.email.trim() } : {}),
    }
    const contacts =
      index != null
        ? company.contacts.map((c, i) => (i === index ? contact : c))
        : [...company.contacts, contact]
    edit.mutate(
      { contacts },
      {
        onSuccess: () => {
          toast.success('Сохранено')
          onClose()
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось сохранить'),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={index != null ? 'Контакт' : 'Новый контакт'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={edit.isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={edit.isPending}>
            {edit.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Имя" {...(nameError ? { error: nameError } : {})}>
          {(props) => (
            <Input {...props} value={form.name} onChange={(e) => set({ name: e.target.value })} />
          )}
        </Field>
        <Field label="Должность">
          {(props) => (
            <Input
              {...props}
              value={form.job_title ?? ''}
              onChange={(e) => set({ job_title: e.target.value })}
            />
          )}
        </Field>
        <Field label="Телефон">
          {(props) => (
            <Input
              {...props}
              value={form.phone ?? ''}
              inputMode="tel"
              onChange={(e) => set({ phone: e.target.value })}
            />
          )}
        </Field>
        <Field label="Email">
          {(props) => (
            <Input
              {...props}
              value={form.email ?? ''}
              inputMode="email"
              onChange={(e) => set({ email: e.target.value })}
            />
          )}
        </Field>
      </div>
    </Dialog>
  )
}
